/**
 * 统一错误提示。
 *
 * 后台此前没有任何全局错误展现：`http.ts` 只 `throw`，而大量写操作是
 * `try { … } finally {}`（**没有 catch**），失败只会进 console —— 用户点了按钮毫无反应。
 * 这里提供两层保障：
 *  1. `installGlobalErrorHandlers` 捕获未处理 Promise 拒绝与组件内异常（覆盖所有漏 catch 的写操作）；
 *  2. 页面里必要的显式 `catch` 可用 `createErrorReporter` 复用同一套文案与去重。
 * 已自行提示的页面不会重复触发（因为异常已被其 catch 消费）。
 */

export interface BackendErrorLike {
  message?: string;
  code?: number;
  error?: string;
  traceId?: string;
}

/** 组装给运营看的错误文案：消息 + 错误码 + 追踪号（便于凭 traceId 查日志）。 */
export function describeError(error: unknown): string {
  if (!error) return '操作失败，请稍后重试';
  if (typeof error === 'string') return error;
  const value = error as BackendErrorLike;
  const message = typeof value.message === 'string' && value.message.trim() ? value.message : '操作失败，请稍后重试';
  const extra: string[] = [];
  if (value.error) extra.push(String(value.error));
  else if (typeof value.code === 'number' && value.code !== 0) extra.push(`错误码 ${value.code}`);
  if (value.traceId) extra.push(`追踪 ${value.traceId}`);
  return extra.length ? `${message}（${extra.join(' · ')}）` : message;
}

export interface ErrorReporterOptions {
  notify: (message: string) => void;
  /** 相同文案在此窗口内只提示一次，避免批量请求失败时刷屏。 */
  dedupeWindowMs?: number;
  now?: () => number;
}

export function createErrorReporter(options: ErrorReporterOptions) {
  const windowMs = options.dedupeWindowMs ?? 3_000;
  const now = options.now ?? (() => Date.now());
  const lastShown = new Map<string, number>();
  return (error: unknown): string => {
    const message = describeError(error);
    const at = now();
    const previous = lastShown.get(message);
    if (previous !== undefined && at - previous < windowMs) return message;
    lastShown.set(message, at);
    options.notify(message);
    return message;
  };
}

export interface GlobalErrorTarget {
  config?: { errorHandler?: ((error: unknown) => void) | null };
  addEventListener?: (type: string, listener: (event: any) => void) => void;
}

/** 挂上 Vue 组件异常与未处理 Promise 拒绝的统一提示。返回卸装函数（测试用）。 */
export function installGlobalErrorHandlers(
  report: (error: unknown) => void,
  target: GlobalErrorTarget = (typeof window !== 'undefined' ? window : {}),
): () => void {
  const onRejection = (event: any) => {
    report(event?.reason ?? event);
    // 阻止控制台噪音；提示已经给出
    if (typeof event?.preventDefault === 'function') event.preventDefault();
  };
  target.addEventListener?.('unhandledrejection', onRejection);
  const previous = target.config?.errorHandler ?? null;
  if (target.config) target.config.errorHandler = (error: unknown) => report(error);
  return () => {
    if (target.config) target.config.errorHandler = previous;
  };
}
